import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { MemberDetailsProvider } from "components/context/MemberDetailsContext";
import HttpErrorProvider from "./components/context/HttpErrorProvider";
import MemberNavigationLayout from "./components/layout/MemberNavigationLayout";
import MemberLogin from "./pages/MemberLogin";
import MemberLoginSetup from "./pages/MemberLoginSetup";
import ResetPassword from "./pages/ResetPassword";
import SetupPassword from "./pages/SetupPassword";
import MemberActivity from "./pages/MemberActivity";
import MemberTasks from "./pages/MemberTasks";
import MemberTask from "./pages/MemberTask";
import MemberTasksUrlQuickAdd from "./pages/MemberTaskUrlQuickAdd";
import FamilyAddress from "./pages/FamilyAddress";
import SecretWrapper from "./pages/SecretWrapper";
import MemberSettings from "./pages/MemberSettings";
import CalendarEventRedirect from "./pages/CalendarEventRedirect";
import TokenRedirect from "./components/common/TokenRedirect";
import FamilyExportDownload from "./pages/FamilyExportDownload";
import AdvisorPublicProfile from "./pages/AdvisorPublicProfile";
import NuxContextProvider from "./components/context/AdvisorNuxContext";
import NuxWrapper from "./pages/nux/NuxWrapper";
import Landing from "./pages/nux/member/Landing";
import Error from "./pages/nux/member/Error";
import PersonalDetails from "./pages/nux/member/MemberDetails";
import PlanDetails from "./pages/nux/member/MemberPlan";
import MemberContacts from "./pages/nux/member/MemberContacts";
import NuxMemberTasks from "./pages/nux/member/MemberTasks";
import Finish from "./pages/nux/member/Finish";
import { USER_NUX_TYPE } from "./components/nux/constants";
import CalendarSettings from "./pages/CalendarSettings";
import { AccessPage, SecretsRoute } from "./pages/Access";
import MemberDocument from "./pages/MemberDocument";
import MemberDocuments from "./pages/MemberDocuments";
import DocumentTemplate from "./pages/DocumentTemplate";
const NUX_ROUTES = [
  { path: "/welcome/landing", component: Landing },
  { path: "/welcome/personal-details", component: PersonalDetails },
  { path: "/welcome/plan-details", component: PlanDetails },
  { path: "/welcome/contacts", component: MemberContacts },
  { path: "/welcome/tasks", component: NuxMemberTasks },
  { path: "/welcome/finish", component: Finish },
  { path: "/welcome/error", component: Error },
];
import { AppBarSxProvider } from "./components/context/AppBarContext";
import { GlobalModalProvider } from "./components/context/GlobalModalContext";
import MemberAdvisorProfile from "./pages/MemberAdvisorProfile";
import Referrals from "./pages/Referrals";
import CalendarPage from "./pages/MemberCalendar";
import TaskDiscovery from "./pages/TaskDiscovery";
import Signup from "./pages/signup/Signup";
import Form from "./pages/Form";
import MemberVideoCall from "./pages/MemberVideoCall";
import MemberHome from "./pages/MemberHome";
import MemberEmails from "./pages/MemberEmails";
import MemberTaskSubtaskDetail from "./pages/MemberTaskSubtaskDetail";
import MemberDelegations from "./pages/MemberDelegations";
import MemberDelegationDetail from "./pages/MemberDelegationDetail";
import MemberScheduledReminders from "./pages/MemberScheduledReminders";
import MemberConversations from "./pages/MemberConversations";
import MemberInspiration from "./pages/MemberInspiration";
import Checkout from "pages/checkout/Checkout";
import ConsultationSchedule from "pages/checkout/ConsultationSchedule";
import DigitalSubscriptionUpgrade from "pages/checkout/DigitalSubscriptionUpgrade";
import { UnpaidBannerProvider } from "components/context/UnpaidBannerContext";
import MemberReactivate from "./pages/MemberReactivate";
import MemberPreviewExpired from "./pages/MemberPreviewExpired";
import AccountSetup from "./pages/AccountSetup";
import RequireAuth from "./components/context/RequireAuth";

const NoMatch = () => {
  return (
    <div>
      <h2>Nothing to see here!</h2>
    </div>
  );
};

const MEMBER_SITE_TITLE = "Member";

export default () => {
  const location = useLocation();
  useEffect(() => {
    document.title = MEMBER_SITE_TITLE;
  }, []);

  return (
    <Routes>
      <Route
        path={"/join/:step?"}
        element={
          <HttpErrorProvider>
            <Signup noBillingFlow={true} />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/signup/:step?"}
        element={
          <HttpErrorProvider>
            <Signup noBillingFlow={false} />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/get-started/:step?"}
        element={
          <HttpErrorProvider>
            <Signup variant="get-started" />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/upgrade/:step?"}
        element={
          <HttpErrorProvider>
            <Checkout />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/digital-upgrade"}
        element={
          <HttpErrorProvider>
            <DigitalSubscriptionUpgrade />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/upgrade-select/:step?"}
        element={
          <HttpErrorProvider>
            <Checkout includeMembershipStep={true} />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/consultation/:step?"}
        element={
          <HttpErrorProvider>
            <ConsultationSchedule />
          </HttpErrorProvider>
        }
      />
      <Route path={"/member-login"} element={<MemberLogin />} />
      <Route
        path={"/member-login/link"}
        element={<MemberLogin linkLogin={true} />}
      />
      <Route path={"/forgot-password"} element={<ResetPassword />} />
      <Route
        path={"/setup-account/:token?"}
        element={<SetupPassword isAccountSetup={true} />}
      />
      <Route
        path={"/reset-password/:token?"}
        element={<SetupPassword isAccountSetup={false} />}
      />
      <Route
        path={"/member-login-setup/:token?"}
        element={<MemberLoginSetup />}
      />
      <Route path={"/token-login/:token?"} element={<TokenRedirect />} />
      <Route
        path={"/export/:token"}
        element={
          <HttpErrorProvider>
            <FamilyExportDownload />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/account-setup"}
        element={
          <HttpErrorProvider>
            <GlobalModalProvider>
              <RequireAuth loginRedirectPath="/member-login">
                <AccountSetup />
              </RequireAuth>
            </GlobalModalProvider>
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/reactivate"}
        element={
          <HttpErrorProvider>
            <MemberReactivate />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/preview-expired"}
        element={
          <HttpErrorProvider>
            <MemberPreviewExpired />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/advisor/:profilePath"}
        element={
          <HttpErrorProvider>
            <AdvisorPublicProfile />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/datebook/event"}
        element={
          <HttpErrorProvider>
            <CalendarEventRedirect />
          </HttpErrorProvider>
        }
      />
      <Route
        path={"/welcome"}
        element={
          <HttpErrorProvider>
            <NuxContextProvider userType={USER_NUX_TYPE.MEMBER}>
              <NuxWrapper userType={USER_NUX_TYPE.MEMBER} />
            </NuxContextProvider>
          </HttpErrorProvider>
        }
      >
        {NUX_ROUTES.map(({ path, component: Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
      </Route>
      <Route
        path="/"
        element={
          <AppBarSxProvider>
            <GlobalModalProvider>
              <HttpErrorProvider>
                <MemberDetailsProvider>
                  {/* Added UnpaidBannerProvider so it's available to NavigationLayout when determining the mobile install prompt. */}
                  <UnpaidBannerProvider>
                    <MemberNavigationLayout />
                  </UnpaidBannerProvider>
                </MemberDetailsProvider>
              </HttpErrorProvider>
            </GlobalModalProvider>
          </AppBarSxProvider>
        }
      >
        <Route
          index
          element={<Navigate to={"home"} state={{ ...location.state }} />}
        />
        <Route path={"/home"} element={<MemberHome />} />
        <Route path={"/activity"} element={<MemberActivity />} />
        <Route path={"/tasks"} element={<MemberTasks />} />
        <Route path={"/tasks/add"} element={<MemberTask addTask={true} />} />
        <Route
          path={"/tasks/add/inspired/:taskRef"}
          element={<MemberTasksUrlQuickAdd />}
        />
        <Route path={"/calendar/:eventRef?"} element={<CalendarPage />} />
        <Route path={"/tasks/:taskRef"} element={<MemberTask />} />
        <Route
          path={"/tasks/subtasks/:ref"}
          element={<MemberTaskSubtaskDetail />}
        />
        <Route path={"/settings/address"} element={<FamilyAddress />} />
        <Route path={"/invite"} element={<Referrals />} />
        <Route
          path={"/task-discovery"}
          element={<TaskDiscovery accountType="member" />}
        />
        <Route path={"/secret/:secretRef"} element={<SecretWrapper />} />
        <Route path={"/secret/"} element={<SecretWrapper />} />
        <Route path={"/settings/"} element={<MemberSettings />} />
        <Route
          path={"/profile/:profileRef"}
          element={<MemberAdvisorProfile />}
        />
        <Route
          path={"/settings/calendars/:calendarType?"}
          element={<CalendarSettings accountType="member" />}
        />
        <Route
          path={"/settings/integrations"}
          element={<Navigate to={"/access"} replace />}
        />
        <Route path={"/access"} element={<AccessPage />} />
        <Route path={"/secrets"} element={<SecretsRoute />} />
        <Route path={"/documents"} element={<MemberDocuments />} />
        <Route
          path={"/templates/open/:uuid"}
          element={<DocumentTemplate mode="open" />}
        />
        <Route
          path={"/templates/create/:uuid"}
          element={<DocumentTemplate mode="create" />}
        />
        <Route path={"/documents/:documentRef"} element={<MemberDocument />} />
        <Route path={"/delegations"} element={<MemberDelegations />} />
        <Route
          path={"/delegations/:ref"}
          element={<MemberDelegationDetail />}
        />
        <Route
          path={"/conversations/:view?/:ref?"}
          element={<MemberConversations />}
        />
        <Route
          path={"/scheduled-reminders"}
          element={<MemberScheduledReminders />}
        />
        <Route path={"/inspiration"} element={<MemberInspiration />} />
        <Route
          path={"/forms/complete/:documentRef"}
          element={<Form mode="complete" />}
        />
        <Route path={"/video/:uuid"} element={<MemberVideoCall />} />
        <Route path={"/emails/:emailRef"} element={<MemberEmails />} />
        <Route path="*" element={<NoMatch />} />
      </Route>
    </Routes>
  );
};
